let nextQuantityKey = 1;

const newKey = () => {
    nextQuantityKey += 1;
    return `q${nextQuantityKey - 1}`;
};

const toQty = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
};

/** 수량 한 줄. 같은 품목을 PCS·CTN처럼 여러 단위로 적을 때 한 단위가 한 줄이다. */
export function newQuantity(partial = {}) {
    return { key: newKey(), qty: null, uom: '', uom_raw: null, is_primary: false, ...partial };
}

/** 대표 수량. 배분·잔량·합계 대조는 이 한 줄로 한다. 표시가 없으면 첫 줄을 대표로 본다. */
export function primaryQuantity(quantities = []) {
    return quantities.find((q) => q.is_primary) ?? quantities[0] ?? null;
}

/** 대표가 정확히 하나가 되게 맞춘다. 없으면 첫 줄, 여럿이면 처음 표시된 줄만 남긴다. */
function normalizePrimary(quantities) {
    if (quantities.length === 0) return quantities;
    const index = Math.max(0, quantities.findIndex((q) => q.is_primary));
    return quantities.map((q, i) => (q.is_primary === (i === index) ? q : { ...q, is_primary: i === index }));
}

/** 라인의 qty·qty_uom은 대표 수량을 그대로 비춘 값이다. 그리드·검증·합계는 이 두 칸을 읽는다. */
function withPrimaryMirror(line, quantities) {
    const primary = primaryQuantity(quantities);
    return { ...line, quantities, qty: primary ? primary.qty : null, qty_uom: primary ? primary.uom : '' };
}

/** 수량 목록이 없는 라인(새 행·옛 초안·파서 원본)은 qty·qty_uom 한 쌍을 대표 수량 한 줄로 옮긴다. */
export function ensureQuantities(line) {
    if (Array.isArray(line.quantities) && line.quantities.length > 0) {
        return withPrimaryMirror(line, normalizePrimary(line.quantities.map((q) => ({ ...q, key: q.key ?? newKey(), qty: toQty(q.qty), uom: q.uom ?? '' }))));
    }
    const hasLegacy = toQty(line.qty) !== null || Boolean(line.qty_uom);
    const quantities = hasLegacy ? [newQuantity({ qty: toQty(line.qty), uom: line.qty_uom ?? '', uom_raw: line.qty_uom_raw ?? null, is_primary: true })] : [newQuantity({ is_primary: true })];
    return withPrimaryMirror(line, quantities);
}

/** 그리드의 Qty·UOM 칸을 고치면 대표 수량 줄이 바뀐다. */
export function updatePrimaryFromCells(line) {
    const base = ensureQuantities(line);
    const index = base.quantities.findIndex((q) => q.is_primary);
    const quantities = base.quantities.map((q, i) => (i === index ? { ...q, qty: toQty(line.qty), uom: line.qty_uom ?? '' } : q));
    return withPrimaryMirror(line, quantities);
}

export function addQuantity(line, partial = {}) {
    const base = ensureQuantities(line);
    return withPrimaryMirror(line, [...base.quantities, newQuantity(partial)]);
}

export function updateQuantity(line, key, patch) {
    const base = ensureQuantities(line);
    const quantities = base.quantities.map((q) => (q.key === key ? { ...q, ...patch, ...(patch.qty !== undefined ? { qty: toQty(patch.qty) } : {}) } : q));
    return withPrimaryMirror(line, quantities);
}

/** 수량 줄을 지운다. 대표 줄을 지우면 남은 첫 줄이 대표가 된다. 마지막 한 줄은 지우지 않는다. */
export function removeQuantity(line, key) {
    const base = ensureQuantities(line);
    if (base.quantities.length <= 1) return base;
    return withPrimaryMirror(line, normalizePrimary(base.quantities.filter((q) => q.key !== key)));
}

export function setPrimaryQuantity(line, key) {
    const base = ensureQuantities(line);
    return withPrimaryMirror(line, base.quantities.map((q) => ({ ...q, is_primary: q.key === key })));
}

/** 대표가 아닌 수량 줄. 그리드·아이템 표에 "+ 10 CTN"처럼 덧붙여 보여준다. */
export function secondaryQuantities(quantities = []) {
    const primary = primaryQuantity(quantities);
    return quantities.filter((q) => q !== primary);
}

export function formatQuantity(q) {
    const qty = toQty(q.qty);
    return `${qty === null ? '—' : qty.toLocaleString()} ${q.uom || ''}`.trim();
}

const unitOf = (q) => String(q.uom ?? '').trim().toUpperCase();

/** 라인에 적힌 한 단위의 수량 합. 그 단위 행이 없으면 null. 카톤 수는 CTN 행, 낱개 수는 PCS 행으로 받는다. */
export function quantityIn(line, uom) {
    const rows = (line.quantities ?? []).filter((q) => unitOf(q) === uom && toQty(q.qty) !== null);
    return rows.length ? rows.reduce((sum, q) => sum + toQty(q.qty), 0) : null;
}
